import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  RouteCard,
  RouteCardOperation,
  RouteCardOperationInput,
  WorkInstruction,
  ToolFixture,
  RouteCardDocument,
  Operation,
  Client,
  Part,
  RouteCardStatus,
  SearchRouteCardsRequest,
  SearchRouteCardsResponse,
  CreateRouteCardRequest,
} from '../../services/routeCardApi';

interface RouteCardState {
  // Route Cards
  routeCards: RouteCard[];
  selectedRouteCard: RouteCard | null;
  loading: boolean;
  error: string | null;

  // Pagination
  currentPage: number;
  pageSize: number;
  totalCount: number;

  // Filters
  searchTerm: string;
  clientFilter: string | null;
  partFilter: string | null;
  statusFilter: number | null;

  // Dropdown data
  clients: Client[];
  parts: Part[];
  operations: Operation[];
  routeCardStatuses: RouteCardStatus[];
  loadingDropdowns: boolean;

  // Operations management
  selectedOperations: RouteCardOperation[];
  operationsLoading: boolean;
  selectedOperationId: string | null;

  // Operation details
  operationInputs: RouteCardOperationInput[];
  workInstructions: WorkInstruction[];
  toolFixtures: ToolFixture[];
  operationDetailsLoading: boolean;

  // Documents
  documents: RouteCardDocument[];
  documentsLoading: boolean;

  // UI State
  showOperationsPanel: boolean;
  showStatusModal: boolean;
  statusModalData: { routeCardId: string; action: 'approve' | 'reject' | 'pending' } | null;
  draggedOperation: RouteCardOperation | null;
}

const initialState: RouteCardState = {
  routeCards: [],
  selectedRouteCard: null,
  loading: false,
  error: null,

  currentPage: 1,
  pageSize: 25,
  totalCount: 0,

  searchTerm: '',
  clientFilter: null,
  partFilter: null,
  statusFilter: null,

  clients: [],
  parts: [],
  operations: [],
  routeCardStatuses: [],
  loadingDropdowns: false,

  selectedOperations: [],
  operationsLoading: false,
  selectedOperationId: null,

  operationInputs: [],
  workInstructions: [],
  toolFixtures: [],
  operationDetailsLoading: false,

  documents: [],
  documentsLoading: false,

  showOperationsPanel: false,
  showStatusModal: false,
  statusModalData: null,
  draggedOperation: null,
};

const routeCardSlice = createSlice({
  name: 'routeCard',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setDropdownsLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingDropdowns = action.payload;
    },

    setOperationsLoading: (state, action: PayloadAction<boolean>) => {
      state.operationsLoading = action.payload;
    },

    setOperationDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.operationDetailsLoading = action.payload;
    },

    setDocumentsLoading: (state, action: PayloadAction<boolean>) => {
      state.documentsLoading = action.payload;
    },

    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    // Route Cards CRUD
    fetchRouteCardsStart: (state, action: PayloadAction<SearchRouteCardsRequest | undefined>) => {
      state.loading = true;
      state.error = null;
    },

    fetchRouteCardsSuccess: (state, action: PayloadAction<SearchRouteCardsResponse>) => {
      state.loading = false;
      state.routeCards = action.payload.data;
      state.totalCount = action.payload.totalCount;
      state.currentPage = action.payload.pageNum;
      state.pageSize = action.payload.pageSize;
    },

    fetchRouteCardsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Single Route Card
    fetchRouteCardByIdStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },

    fetchRouteCardByIdSuccess: (state, action: PayloadAction<RouteCard>) => {
      state.loading = false;
      state.selectedRouteCard = action.payload;
    },

    fetchRouteCardByIdFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Create Route Card
    createRouteCardStart: (state, action: PayloadAction<CreateRouteCardRequest>) => {
      state.loading = true;
      state.error = null;
    },

    createRouteCardSuccess: (state, action: PayloadAction<RouteCard>) => {
      state.loading = false;
      state.routeCards.unshift(action.payload);
      state.totalCount += 1;
    },

    createRouteCardFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update Route Card
    updateRouteCardStart: (state, action: PayloadAction<Partial<RouteCard>>) => {
      state.loading = true;
      state.error = null;
    },

    updateRouteCardSuccess: (state, action: PayloadAction<RouteCard>) => {
      state.loading = false;
      const index = state.routeCards.findIndex(rc => rc.id === action.payload.id);
      if (index !== -1) {
        state.routeCards[index] = action.payload;
      }
      if (state.selectedRouteCard?.id === action.payload.id) {
        state.selectedRouteCard = action.payload;
      }
    },

    updateRouteCardFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete Route Card
    deleteRouteCardStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },

    deleteRouteCardSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.routeCards = state.routeCards.filter(rc => rc.id !== action.payload);
      state.totalCount -= 1;
      if (state.selectedRouteCard?.id === action.payload) {
        state.selectedRouteCard = null;
      }
    },

    deleteRouteCardFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Dropdown data
    fetchDropdownsStart: (state) => {
      state.loadingDropdowns = true;
    },

    fetchDropdownsSuccess: (state, action: PayloadAction<{
      clients: Client[];
      parts: Part[];
      operations: Operation[];
      routeCardStatuses: RouteCardStatus[];
    }>) => {
      console.log('RouteCard Reducer: fetchDropdownsSuccess called with:', action.payload);
      console.log('RouteCard Reducer: Received parts array:', action.payload.parts);
      console.log('RouteCard Reducer: Parts array length:', action.payload.parts?.length);
      console.log('RouteCard Reducer: Parts is array:', Array.isArray(action.payload.parts));
      
      state.loadingDropdowns = false;
      state.clients = action.payload.clients;
      state.parts = action.payload.parts;
      state.operations = action.payload.operations;
      state.routeCardStatuses = action.payload.routeCardStatuses;
      
      console.log('RouteCard Reducer: State updated. Parts length:', state.parts.length);
    },

    fetchDropdownsFailure: (state, action: PayloadAction<string>) => {
      state.loadingDropdowns = false;
      state.error = action.payload;
    },

    // Operations management
    fetchOperationsStart: (state, action: PayloadAction<string>) => {
      state.operationsLoading = true;
    },

    fetchOperationsSuccess: (state, action: PayloadAction<RouteCardOperation[]>) => {
      state.operationsLoading = false;
      state.selectedOperations = action.payload;
    },

    fetchOperationsFailure: (state, action: PayloadAction<string>) => {
      state.operationsLoading = false;
      state.error = action.payload;
    },

    // Add Operation
    addOperationStart: (state, action: PayloadAction<{ routeCardId: string; operation: Omit<RouteCardOperation, 'id' | 'RCTId'> }>) => {
      // Optimistic update
    },

    addOperationSuccess: (state, action: PayloadAction<RouteCardOperation>) => {
      state.selectedOperations.push(action.payload);
      // Update route card operations if selected
      if (state.selectedRouteCard) {
        state.selectedRouteCard.Operations = state.selectedOperations;
      }
    },

    addOperationFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Update Operation
    updateOperationStart: (state, action: PayloadAction<RouteCardOperation>) => {
      // Optimistic update
      const index = state.selectedOperations.findIndex(op => op.id === action.payload.id);
      if (index !== -1) {
        state.selectedOperations[index] = action.payload;
      }
    },

    updateOperationSuccess: (state, action: PayloadAction<RouteCardOperation>) => {
      const index = state.selectedOperations.findIndex(op => op.id === action.payload.id);
      if (index !== -1) {
        state.selectedOperations[index] = action.payload;
      }
    },

    updateOperationFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Delete Operation
    deleteOperationStart: (state, action: PayloadAction<string>) => {
      // Optimistic update
      state.selectedOperations = state.selectedOperations.filter(op => op.id !== action.payload);
    },

    deleteOperationSuccess: (state, action: PayloadAction<string>) => {
      state.selectedOperations = state.selectedOperations.filter(op => op.id !== action.payload);
    },

    deleteOperationFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Reorder Operations
    reorderOperationsStart: (state, action: PayloadAction<{ routeCardId: string; operations: { id: string; sequence: number }[] }>) => {
      // Update sequence numbers locally
      action.payload.operations.forEach(({ id, sequence }) => {
        const operation = state.selectedOperations.find(op => op.id === id);
        if (operation) {
          operation.Sequence = sequence;
        }
      });
      // Sort by sequence
      state.selectedOperations.sort((a, b) => a.Sequence - b.Sequence);
    },

    reorderOperationsSuccess: (state, action: PayloadAction<RouteCardOperation[]>) => {
      state.selectedOperations = action.payload;
    },

    reorderOperationsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Operation Details
    fetchOperationDetailsStart: (state, action: PayloadAction<string>) => {
      state.operationDetailsLoading = true;
      state.selectedOperationId = action.payload;
    },

    fetchOperationDetailsSuccess: (state, action: PayloadAction<{
      inputs: RouteCardOperationInput[];
      workInstructions: WorkInstruction[];
      toolFixtures: ToolFixture[];
    }>) => {
      state.operationDetailsLoading = false;
      state.operationInputs = action.payload.inputs;
      state.workInstructions = action.payload.workInstructions;
      state.toolFixtures = action.payload.toolFixtures;
    },

    fetchOperationDetailsFailure: (state, action: PayloadAction<string>) => {
      state.operationDetailsLoading = false;
      state.error = action.payload;
    },

    // Documents
    fetchDocumentsStart: (state, action: PayloadAction<string>) => {
      state.documentsLoading = true;
    },

    fetchDocumentsSuccess: (state, action: PayloadAction<RouteCardDocument[]>) => {
      state.documentsLoading = false;
      state.documents = action.payload;
    },

    fetchDocumentsFailure: (state, action: PayloadAction<string>) => {
      state.documentsLoading = false;
      state.error = action.payload;
    },

    // Status Management
    approveRouteCardStart: (state, action: PayloadAction<{ id: string; comment?: string }>) => {
      state.loading = true;
    },

    approveRouteCardSuccess: (state, action: PayloadAction<RouteCard>) => {
      state.loading = false;
      const index = state.routeCards.findIndex(rc => rc.id === action.payload.id);
      if (index !== -1) {
        state.routeCards[index] = action.payload;
      }
      if (state.selectedRouteCard?.id === action.payload.id) {
        state.selectedRouteCard = action.payload;
      }
    },

    approveRouteCardFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    rejectRouteCardStart: (state, action: PayloadAction<{ id: string; comment?: string }>) => {
      state.loading = true;
    },

    rejectRouteCardSuccess: (state, action: PayloadAction<RouteCard>) => {
      state.loading = false;
      const index = state.routeCards.findIndex(rc => rc.id === action.payload.id);
      if (index !== -1) {
        state.routeCards[index] = action.payload;
      }
      if (state.selectedRouteCard?.id === action.payload.id) {
        state.selectedRouteCard = action.payload;
      }
    },

    rejectRouteCardFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Filters and search
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1; // Reset to first page when searching
    },

    setClientFilter: (state, action: PayloadAction<string | null>) => {
      state.clientFilter = action.payload;
      state.currentPage = 1;
      // Clear parts filter when client changes
      if (action.payload !== state.clientFilter) {
        state.partFilter = null;
        // Fetch parts for the selected client
      }
    },

    setPartFilter: (state, action: PayloadAction<string | null>) => {
      state.partFilter = action.payload;
      state.currentPage = 1;
    },

    setStatusFilter: (state, action: PayloadAction<number | null>) => {
      state.statusFilter = action.payload;
      state.currentPage = 1;
    },

    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },

    // UI State
    toggleOperationsPanel: (state) => {
      state.showOperationsPanel = !state.showOperationsPanel;
    },

    setOperationsPanel: (state, action: PayloadAction<boolean>) => {
      state.showOperationsPanel = action.payload;
    },

    openStatusModal: (state, action: PayloadAction<{ routeCardId: string; action: 'approve' | 'reject' | 'pending' }>) => {
      state.showStatusModal = true;
      state.statusModalData = action.payload;
    },

    closeStatusModal: (state) => {
      state.showStatusModal = false;
      state.statusModalData = null;
    },

    // Drag and Drop
    setDraggedOperation: (state, action: PayloadAction<RouteCardOperation | null>) => {
      state.draggedOperation = action.payload;
    },

    // Clear selected data
    clearSelectedRouteCard: (state) => {
      state.selectedRouteCard = null;
      state.selectedOperations = [];
      state.operationInputs = [];
      state.workInstructions = [];
      state.toolFixtures = [];
      state.documents = [];
      state.selectedOperationId = null;
    },

    clearSelectedOperations: (state) => {
      state.selectedOperations = [];
      state.operationInputs = [];
      state.workInstructions = [];
      state.toolFixtures = [];
      state.selectedOperationId = null;
    },

    // Reset state
    resetRouteCardState: (state) => {
      return initialState;
    },
  },
});

export const {
  setLoading,
  setDropdownsLoading,
  setOperationsLoading,
  setOperationDetailsLoading,
  setDocumentsLoading,
  setError,
  clearError,

  fetchRouteCardsStart,
  fetchRouteCardsSuccess,
  fetchRouteCardsFailure,

  fetchRouteCardByIdStart,
  fetchRouteCardByIdSuccess,
  fetchRouteCardByIdFailure,

  createRouteCardStart,
  createRouteCardSuccess,
  createRouteCardFailure,

  updateRouteCardStart,
  updateRouteCardSuccess,
  updateRouteCardFailure,

  deleteRouteCardStart,
  deleteRouteCardSuccess,
  deleteRouteCardFailure,

  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,

  fetchOperationsStart,
  fetchOperationsSuccess,
  fetchOperationsFailure,

  addOperationStart,
  addOperationSuccess,
  addOperationFailure,

  updateOperationStart,
  updateOperationSuccess,
  updateOperationFailure,

  deleteOperationStart,
  deleteOperationSuccess,
  deleteOperationFailure,

  reorderOperationsStart,
  reorderOperationsSuccess,
  reorderOperationsFailure,

  fetchOperationDetailsStart,
  fetchOperationDetailsSuccess,
  fetchOperationDetailsFailure,

  fetchDocumentsStart,
  fetchDocumentsSuccess,
  fetchDocumentsFailure,

  approveRouteCardStart,
  approveRouteCardSuccess,
  approveRouteCardFailure,

  rejectRouteCardStart,
  rejectRouteCardSuccess,
  rejectRouteCardFailure,

  setSearchTerm,
  setClientFilter,
  setPartFilter,
  setStatusFilter,
  setPage,
  setPageSize,

  toggleOperationsPanel,
  setOperationsPanel,
  openStatusModal,
  closeStatusModal,

  setDraggedOperation,

  clearSelectedRouteCard,
  clearSelectedOperations,
  resetRouteCardState,
} = routeCardSlice.actions;

export default routeCardSlice.reducer; 